import { useState, useCallback, useEffect, useRef } from 'react';
import { getStoredToken } from './useAuth';

function buildHeaders(): Record<string, string> {
  const token = getStoredToken();
  return {
    'Authorization': `Bearer ${token ?? ''}`,
    'Content-Type': 'application/json',
  };
}

export interface KeepsakedMessage {
  id: string;
  session_id: string;
  role: string;
  content: string;
  created_at: string;
  session_label: string | null;
  session_type: string;
  /** The owning session's companion identity ('rook' | 'rainer'). Populated
   *  by the gateway's EnrichedMessageRow (transcript.ts) — used by the
   *  cross-identity keepsakes gallery to badge/filter each card. */
  session_identity: string;
}

/**
 * @param sessionId The active session to track per-message keepsake state for.
 * @param identity The active session's OWN companion identity ('rook' |
 *   'rainer'), threaded into every session-scoped keepsake request as
 *   `?companion=`. Without this, requests silently default to 'rook'
 *   server-side (resolveCompanionFromQuery) — which 404s (via
 *   checkSessionOwnership) for any rainer-owned session, since the ownership
 *   guard correctly refuses a mismatched identity. This was the root cause of
 *   "keepsake button blocked in Rainer chats": the client never told the
 *   server which companion it meant, so a legitimate rainer request looked
 *   identical to an IDOR probe and was (correctly) rejected. See
 *   route-idor.test.ts's 'GET keepsakes cross-identity' test — that
 *   IDOR-safe behavior is exactly right and must NOT be weakened; the fix is
 *   the client supplying its own identity, not loosening the guard.
 */
export function useKeepsakes(sessionId: string | null, identity?: string) {
  const [keepsakes, setKeepsakes] = useState<Set<string>>(new Set());
  const [allKeepsakes, setAllKeepsakes] = useState<KeepsakedMessage[]>([]);
  const [loading, setLoading] = useState(false);

  // Stable ref so toggleKeepsake callback doesn't recreate on every Set change
  const keepsakesRef = useRef<Set<string>>(new Set());

  // Keep ref in sync with state
  useEffect(() => {
    keepsakesRef.current = keepsakes;
  }, [keepsakes]);

  // Fetch keepsakes for the active session
  useEffect(() => {
    if (!sessionId || !getStoredToken()) {
      setKeepsakes(new Set());
      keepsakesRef.current = new Set();
      return;
    }
    setLoading(true);
    // ?companion= must match the session's OWN identity — see the identity
    // param doc comment above. Omitting it defaults to 'rook' server-side and
    // 404s for rainer sessions.
    const qs = identity ? `?companion=${encodeURIComponent(identity)}` : '';
    fetch(`/api/sessions/${encodeURIComponent(sessionId)}/keepsakes${qs}`, {
      headers: buildHeaders(),
    })
      .then((res) => (res.ok ? res.json() : []))
      .then((data: KeepsakedMessage[]) => {
        const next = new Set(data.map((m) => m.id));
        keepsakesRef.current = next;
        setKeepsakes(next);
      })
      .catch(() => {
        keepsakesRef.current = new Set();
        setKeepsakes(new Set());
      })
      .finally(() => setLoading(false));
  }, [sessionId, identity]);

  const fetchAllKeepsakes = useCallback(async () => {
    if (!getStoredToken()) return;
    try {
      const res = await fetch('/api/keepsakes', { headers: buildHeaders() });
      if (!res.ok) return;
      const data = (await res.json()) as KeepsakedMessage[];
      setAllKeepsakes(prev => {
        if (prev.length === data.length && prev.every((m, i) => m.id === data[i].id)) return prev;
        return data;
      });
    } catch {
      // ignore
    }
  }, []);

  // toggleKeepsake reads from ref — stable callback, doesn't depend on keepsakes Set
  const toggleKeepsake = useCallback(
    async (messageId: string) => {
      if (!sessionId || !getStoredToken()) return;
      const wasKeepsaked = keepsakesRef.current.has(messageId);
      // Optimistic update via ref + state
      const optimistic = new Set(keepsakesRef.current);
      if (wasKeepsaked) {
        optimistic.delete(messageId);
      } else {
        optimistic.add(messageId);
      }
      keepsakesRef.current = optimistic;
      setKeepsakes(new Set(optimistic));
      try {
        // ?companion= must match the session's OWN identity — see the
        // identity param doc comment on useKeepsakes above. This is the
        // exact fix for "keepsake button blocked in Rainer chats": without
        // it, the PATCH silently defaulted to 'rook' and 404'd against a
        // rainer-owned session, which the catch block below then quietly
        // reverted — looking like the button was "blocked".
        const qs = identity ? `?companion=${encodeURIComponent(identity)}` : '';
        const res = await fetch(
          `/api/sessions/${encodeURIComponent(sessionId)}/messages/${encodeURIComponent(messageId)}${qs}`,
          {
            method: 'PATCH',
            headers: buildHeaders(),
            body: JSON.stringify({ keepsaked: !wasKeepsaked }),
          },
        );
        if (!res.ok) throw new Error('toggle failed');
      } catch {
        // Revert on failure
        const reverted = new Set(keepsakesRef.current);
        if (wasKeepsaked) {
          reverted.add(messageId);
        } else {
          reverted.delete(messageId);
        }
        keepsakesRef.current = reverted;
        setKeepsakes(new Set(reverted));
      }
    },
    [sessionId, identity],
  );

  const isKeepsaked = useCallback(
    (messageId: string): boolean => keepsakesRef.current.has(messageId),
    [],
  );

  return { keepsakes, toggleKeepsake, isKeepsaked, allKeepsakes, fetchAllKeepsakes, loading };
}
